import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Token plumbing — used by AuthProvider on login/logout.
let CURRENT_TOKEN = null;
export function setAuthToken(token) {
  CURRENT_TOKEN = token || null;
  if (CURRENT_TOKEN) {
    api.defaults.headers.common["Authorization"] = `Bearer ${CURRENT_TOKEN}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}
export function getAuthToken() {
  return CURRENT_TOKEN;
}

// On 401 from any protected route, drop the local token so the gate appears.
// 401s from /auth/login are the LoginGate's own "wrong password" signal —
// never reload on those, or the error message gets wiped before it renders.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && !url.includes("/auth/login")) {
      try {
        localStorage.removeItem("crash_auth_token");
        setAuthToken(null);
        if (typeof window !== "undefined" && window.location) {
          // Soft reload to surface the LoginGate.
          window.location.reload();
        }
      } catch (e) {
        console.warn("auth: failed to clear local token", e);
      }
    }
    return Promise.reject(err);
  }
);

export async function createJob() {
  const { data } = await api.post("/jobs");
  return data; // { id, created_at }
}

export async function listJobs() {
  const { data } = await api.get("/jobs");
  return data;
}

export async function uploadFiles(jobId, files, onProgress) {
  const form = new FormData();
  files.forEach((f) => form.append("files", f, f.name));
  const { data } = await api.post(`/jobs/${jobId}/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
}

// Chunked upload — slices each file client-side so we never hit the 100 MB
// (~60 s) ingress proxy ceiling on a single request. Each slice is its own
// short request; only after every slice lands do we call /upload-finalize
// to stitch them back together on the server.
const CHUNK_SIZE = 3 * 1024 * 1024; // 3 MB — well under Mongo's 16 MB doc cap

function _uploadId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  const buf = new Uint8Array(16);
  (crypto || window.msCrypto).getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function uploadFilesChunked(jobId, files, onProgress) {
  const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
  let uploadedBytes = 0;
  const uploads = [];
  const emit = (extra = {}) => {
    if (!onProgress) return;
    const pct = Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
    onProgress(pct, extra);
  };

  for (let fIdx = 0; fIdx < files.length; fIdx++) {
    const file = files[fIdx];
    const upload_id = _uploadId();
    const total_chunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    for (let i = 0; i < total_chunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const form = new FormData();
      form.append("upload_id", upload_id);
      form.append("file_name", file.name);
      form.append("chunk_index", String(i));
      form.append("total_chunks", String(total_chunks));
      form.append("chunk", blob, `chunk_${i}`);

      // Retry per chunk so a single proxy hiccup doesn't fail the whole upload.
      let lastErr = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          await api.post(`/jobs/${jobId}/upload-chunk`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              if (e.total && onProgress) {
                const pct = Math.min(
                  100,
                  Math.round(((uploadedBytes + (e.loaded || 0)) / totalBytes) * 100)
                );
                onProgress(pct, {
                  file: file.name,
                  fileIndex: fIdx + 1,
                  fileCount: files.length,
                  chunk: i + 1,
                  totalChunks: total_chunks,
                });
              }
            },
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          // Don't retry on 4xx — that's our bug, not flaky network.
          const code = err?.response?.status;
          if (code && code >= 400 && code < 500) break;
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
        }
      }
      if (lastErr) throw lastErr;

      uploadedBytes += blob.size;
      emit({
        file: file.name,
        fileIndex: fIdx + 1,
        fileCount: files.length,
        chunk: i + 1,
        totalChunks: total_chunks,
      });
    }
    uploads.push({ upload_id, file_name: file.name });
  }

  const { data } = await api.post(`/jobs/${jobId}/upload-finalize`, { uploads });
  return data;
}

export async function getJob(jobId) {
  const { data } = await api.get(`/jobs/${jobId}`);
  return data;
}

export async function listReports(jobId, { injury_code, transported, search, only_qualifying = true, status } = {}) {
  const { data } = await api.get(`/jobs/${jobId}/reports`, {
    params: {
      only_qualifying,
      injury_code: injury_code || undefined,
      transported: transported === undefined ? undefined : transported,
      search: search || undefined,
      status: status || undefined,
    },
  });
  return data;
}

export async function retryReport(reportId) {
  const { data } = await api.post(`/reports/${reportId}/retry`);
  return data;
}

export function pdfUrl(reportId) {
  const t = getAuthToken();
  const q = t ? `?token=${encodeURIComponent(t)}` : "";
  return `${API}/reports/${reportId}/pdf${q}`;
}

export function csvExportUrl(jobId) {
  const t = getAuthToken();
  const q = t ? `?token=${encodeURIComponent(t)}` : "";
  return `${API}/jobs/${jobId}/export.csv${q}`;
}

export function openPdf(reportId) {
  // Tries every available method to deliver the PDF to the user:
  //   1. Open in a new tab (works in most browsers).
  //   2. Same-tab navigation (browser handles Content-Disposition: attachment).
  //   3. Copy the URL to the clipboard as a last-resort fallback.
  const url = pdfUrl(reportId);
  try {
    const w = window.open(url, "_blank", "noopener");
    if (w && !w.closed) return { method: "new-tab", url };
  } catch (e) {
    console.warn("openPdf: new-tab failed", e);
  }
  try {
    window.location.assign(url);
    return { method: "same-tab", url };
  } catch (e) {
    console.warn("openPdf: same-tab failed", e);
  }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
      return { method: "clipboard", url };
    }
  } catch (e) {
    console.warn("openPdf: clipboard failed", e);
  }
  return { method: "failed", url };
}

export async function deleteJob(jobId) {
  await api.delete(`/jobs/${jobId}`);
}

export async function getBudgetStatus() {
  const { data } = await api.get("/budget/status");
  return data;
}

export async function getQueueStatus() {
  const { data } = await api.get("/queue-status");
  return data;
}

export async function resetBudget() {
  const { data } = await api.post("/budget/reset");
  return data;
}

export async function resumeJob(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/resume`);
  return data;
}

export async function retryFailedJob(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/retry-failed`);
  return data;
}

export async function reEnrichJob(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/re-enrich`);
  return data;
}
